import React from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { AlertTriangle, Trash2, Info, CheckCircle2 } from "lucide-react";

/**
 * ConfirmDialog — shared confirmation prompt used across the app.
 *
 * Props:
 *   open          bool — controlled
 *   onOpenChange  (o) => void
 *   variant       'destructive' (red) | 'warning' (amber) | 'info' (navy) | 'success' (green) — default 'destructive'
 *   title         string — what you're about to do ("Delete this project?")
 *   description   string — what will happen and any consequences
 *   confirmLabel  string — primary button label (e.g. "Delete project")
 *   cancelLabel   string — cancel button label (default "Cancel")
 *   onConfirm     fn — invoked when the user confirms
 *   busy          bool — disables the confirm button while an in-flight action is running
 *   testid        data-testid
 */
export default function ConfirmDialog({
    open,
    onOpenChange,
    variant = "destructive",
    title,
    description,
    confirmLabel,
    cancelLabel = "Cancel",
    onConfirm,
    busy = false,
    testid = "confirm-dialog",
}) {
    const config = VARIANTS[variant] || VARIANTS.destructive;
    const Icon = config.icon;
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="max-w-md" data-testid={testid}>
                <AlertDialogHeader className="pb-3" style={{ borderBottom: "1px solid var(--asl-divider)" }}>
                    <div className="flex items-start gap-3">
                        <div
                            className="p-2 rounded-lg flex-shrink-0"
                            style={{ background: config.bg, color: config.fg }}
                        >
                            <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <AlertDialogTitle className="text-section-title" data-testid={`${testid}-title`}>
                                {title}
                            </AlertDialogTitle>
                            {description && (
                                <AlertDialogDescription className="text-body-muted mt-2" data-testid={`${testid}-description`}>
                                    {description}
                                </AlertDialogDescription>
                            )}
                        </div>
                    </div>
                </AlertDialogHeader>
                <AlertDialogFooter className="pt-3 flex-row justify-between sm:justify-between" style={{ borderTop: "1px solid var(--asl-divider)" }}>
                    <AlertDialogCancel data-testid={`${testid}-cancel`}>{cancelLabel}</AlertDialogCancel>
                    <AlertDialogAction
                        data-testid={`${testid}-confirm`}
                        onClick={onConfirm}
                        disabled={busy}
                        className={config.actionClass}
                    >
                        {busy ? "Working…" : confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

const VARIANTS = {
    destructive: {
        icon: Trash2,
        bg: "rgba(220, 38, 38, 0.10)",
        fg: "#B41818",
        actionClass: "bg-[color:var(--asl-danger)] hover:bg-red-700 text-white",
    },
    warning: {
        icon: AlertTriangle,
        bg: "rgba(245, 158, 11, 0.12)",
        fg: "#B4630D",
        actionClass: "bg-[color:var(--asl-warning)] hover:bg-amber-600 text-white",
    },
    info: {
        icon: Info,
        bg: "rgba(37, 99, 235, 0.10)",
        fg: "#1D4ED8",
        actionClass: "bg-[color:var(--asl-navy-2)] hover:bg-[color:var(--asl-navy-hi)] text-white",
    },
    success: {
        icon: CheckCircle2,
        bg: "rgba(14, 159, 110, 0.10)",
        fg: "#0E7A55",
        actionClass: "bg-[color:var(--asl-success)] hover:bg-emerald-700 text-white",
    },
};
